import { DITypes } from "@/core/@types/types";
import { inject, injectable } from "inversify";
import { type IEvent } from "../Events/Event";
import {
  type Calendly,
  CalendlyWidgetPrefill,
  IF360Calendly
} from "./F360Calendly";
import "reflect-metadata";

type CustomEventDataPayloadEvent = {
  uri: string;
};

type CustomEventDataPayload = {
  event: CustomEventDataPayloadEvent;
};

type CustomEventData = {
  event?: string;
  payload: CustomEventDataPayload;
};

type CustomMessageEvent = MessageEvent & {
  data: CustomEventData;
};

@injectable()
export class F360CalendlyAdapter implements IF360Calendly {
  private readonly SCRIPT_ID = "calendly-script";
  private readonly document!: Document;
  private calendly?: Calendly;

  //registrar um mapa para os eventos de callback
  private eventHandlerMap: Map<
    (params?: unknown) => void,
    (params?: unknown) => void
  > = new Map();

  constructor(
    @inject(DITypes.EVENT) private readonly event: IEvent,
    _document: Document = document,
    _calendly: Calendly = window.Calendly
  ) {
    this.document = _document ?? document;
    this.calendly = _calendly;
  }

  private isScriptAlreadySet() {
    return this.document.getElementById(this.SCRIPT_ID);
  }

  async setupCalendly() {
    return new Promise((resolve, reject) => {
      try {
        if (this.isScriptAlreadySet()) {
          this.calendly = window.Calendly;
          resolve(true);

          return;
        }
        const src = "https://assets.calendly.com/assets/external/widget.js";
        const script = this.document.createElement("script");
        script.setAttribute("src", src);
        script.setAttribute("type", "text/javascript");
        script.setAttribute("id", this.SCRIPT_ID);
        this.document.head.appendChild(script);
        script.onload = () => {
          this.calendly = window.Calendly;
          resolve(true);
        };
      } catch (e) {
        reject(e);
      }
    });
  }

  private isCalendlyEvent = (e: CustomMessageEvent) => {
    const calendlyURL = "https://calendly.com";

    return e.origin === calendlyURL;
  };

  private reuniaoAgendada(event: CustomMessageEvent): boolean {
    const reuniaoMarcada = "calendly.event_scheduled";

    return (
      typeof event.data.event === "string" &&
      event.data?.event.indexOf(reuniaoMarcada) === 0
    );
  }

  observarReuniaoFoiAgendada<T extends (params: unknown) => void>(callback: T) {
    const handler = (event: unknown) => {
      if (!this.isCalendlyEvent(event as CustomMessageEvent)) return;
      if (!this.reuniaoAgendada(event as CustomMessageEvent)) return;

      callback(
        (event as CustomMessageEvent | undefined)?.data?.payload?.event?.uri
      );
    };

    // registra o evento de callback e salva a referência original para pode excluir
    this.eventHandlerMap.set(callback, handler);
    this.event.listenEvent("message", handler);
  }

  removerEventoReuniaoAgendada<T extends () => void>(callback: T) {
    const handler = this.eventHandlerMap.get(callback);
    if (handler) {
      // busca a callback registrar para excluir
      this.event.removeEvent("message", handler);
      this.eventHandlerMap.delete(callback);
    }
  }

  async createWidget(
    url: string,
    elementId = "calendly",
    prefill?: CalendlyWidgetPrefill<unknown>
  ) {
    await this.calendly?.initInlineWidget({
      url: url,
      resize: true,
      parentElement: this.document.getElementById(elementId),
      prefill,
      utm: {}
    });
  }
}
